import { MedicalPlanMapping } from "Features/MedicalPlanMappings/medicalPlanMappings.types";
import { useGetPlanSuiteCurrentCostsQuery, useGetPlanSuiteRenewalCostsQuery } from "Features/PlanSuites/planSuites.api";
import { PlanSuiteCurrentCosts, PlanSuiteRenewalCosts, WithPlans } from "Features/PlanSuites/planSuites.types";

type UseRenewalSuitesReturn = {
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  planSuitesWithCost: (PlanSuiteRenewalCosts & WithPlans)[];
};

type UseRenewalSuitesProps = {
  medicalPlanMapping: MedicalPlanMapping;
};
// React doesn't like array of hooks, so we have to do this manually up to 5 options
// we can always increase this limit later if needed.
export const useRenewalSuites = ({ medicalPlanMapping }: UseRenewalSuitesProps): UseRenewalSuitesReturn => {
  const { items = [] } = medicalPlanMapping || {};
  const planSuiteIds = items.map(({ planSuiteID }) => planSuiteID);
  const option1 = useGetPlanSuiteRenewalCostsQuery({ planSuiteID: planSuiteIds[0], includeFlags: ["plans"] }, { skip: !planSuiteIds[0] });
  const option2 = useGetPlanSuiteRenewalCostsQuery({ planSuiteID: planSuiteIds[1], includeFlags: ["plans"] }, { skip: !planSuiteIds[1] });
  const option3 = useGetPlanSuiteRenewalCostsQuery({ planSuiteID: planSuiteIds[2], includeFlags: ["plans"] }, { skip: !planSuiteIds[2] });
  const option4 = useGetPlanSuiteRenewalCostsQuery({ planSuiteID: planSuiteIds[3], includeFlags: ["plans"] }, { skip: !planSuiteIds[3] });
  const option5 = useGetPlanSuiteRenewalCostsQuery({ planSuiteID: planSuiteIds[4], includeFlags: ["plans"] }, { skip: !planSuiteIds[4] });

  const options = [option1, option2, option3, option4, option5];
  const planSuitesWithCost = planSuiteIds.map((_id, index) => {
    return options[index]?.data?.planSuite;
  });
  const anyLoading = options.some(({ isLoading }) => isLoading);
  const anyError = options.some(({ isError }) => isError);
  const firstError = options.find(({ error }) => error)?.error;
  return {
    error: firstError,
    isLoading: anyLoading,
    isError: anyError,
    planSuitesWithCost: anyLoading || anyError ? [] : (planSuitesWithCost as (PlanSuiteRenewalCosts & WithPlans)[]),
  };
};

interface UseCurrentSuiteReturn {
  currentSuite: PlanSuiteCurrentCosts & WithPlans;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  renewalSuites: (PlanSuiteRenewalCosts & WithPlans)[];
}

// Helper hook that loads both current suite and renewal suites
export const useCurrentAndRenewalSuites = ({ medicalPlanMapping }): UseCurrentSuiteReturn => {
  const { data, error, isError, isLoading } = useGetPlanSuiteCurrentCostsQuery({ planSuiteID: "current", includeFlags: ["plans"] });
  const currentSuite = data?.planSuite as PlanSuiteCurrentCosts & WithPlans;
  const { isLoading: isRenewalLoading, error: renewalError, isError: isRenewalError, planSuitesWithCost } = useRenewalSuites({ medicalPlanMapping });

  return {
    currentSuite,
    error: error || renewalError,
    isLoading: isLoading || isRenewalLoading,
    isError: isError || isRenewalError,
    renewalSuites: planSuitesWithCost,
  };
};
